/**
 * MarkdownSource — Markdown-Quellmodus für den Collab-Editor (#2242).
 *
 * Der Inhalt eines Collab-Beitrags ist Markdown-Text in TipTap-Absätzen:
 * Agenten schreiben `## Titel` oder `**fett**` wörtlich über doc_apply_text,
 * der Server materialisiert nur den Text, die Leseansicht rendert jeden
 * Absatz mit marked. Es gibt also genau eine Repräsentation. Dieser Modus
 * legt eine Decoration-Ebene darüber, die die Markdown-Zeichen sichtbar
 * lässt, aber wie iA Writer bereits formatiert: Überschriften groß, fett
 * fett, Marker gedimmt, Link-URLs gedimmt, Fences monospaced.
 *
 * Nichts davon ändert das Dokument. Decorations sind reine Darstellung.
 *
 * Außerdem übernimmt die Extension die Tastenkürzel, die StarterKit sonst
 * in ProseMirror-Marks oder -Knoten übersetzen würde (Cmd+B erzeugt eine
 * Bold-Mark, die der Server beim Materialisieren verliert). Hier fügen
 * sie Markdown-Zeichen ein. Die Schema-Knoten von StarterKit bleiben
 * erhalten, weil y-prosemirror unbekannte Knoten aus dem Yjs-Dokument
 * löscht und ältere Beiträge solche Knoten enthalten können.
 */

import { Extension, type Editor } from '@tiptap/core'
import { Plugin, PluginKey, type EditorState, type Transaction } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { Fragment, Slice, type Node as PMNode } from '@tiptap/pm/model'
import {
  MD_TABLE_SKELETON,
  RE_BLOCK_PREFIX,
  initialScanState,
  nextListMarker,
  scanMarkdownParagraph,
  type MarkdownToolbarApi,
} from '../utils/markdownScan'

export interface MarkdownSourceOptions {
  enabled: boolean
}

interface PluginState {
  enabled: boolean
  decorations: DecorationSet
}

const PLUGIN_KEY = new PluginKey<PluginState>('markdownSource')
const TOGGLE_META = 'markdownSource:set'

/** Platzhalter für Inline-Knoten ohne Text (Mention, Bild, Umbruch). */
const OBJECT_CHAR = '￼'

// ---------------------------------------------------------------------------
// Decorations aus dem Scanner
// ---------------------------------------------------------------------------

function textOf(block: PMNode): string {
  let out = ''
  block.forEach((child) => {
    // Ein harter Umbruch ist für den Scanner ein Zeilenende.
    if (child.isText) out += child.text ?? ''
    else if (child.type.name === 'hardBreak') out += '\n'
    else out += OBJECT_CHAR.repeat(child.nodeSize)
  })
  return out
}

function buildDecorations(doc: PMNode): DecorationSet {
  const decos: Decoration[] = []
  const state = initialScanState()
  doc.descendants((node, pos) => {
    if (!node.isTextblock) return true
    const text = textOf(node)
    const res = scanMarkdownParagraph(text, state)
    if (res.block) decos.push(Decoration.node(pos, pos + node.nodeSize, { class: res.block }))
    const base = pos + 1
    for (const s of res.spans) decos.push(Decoration.inline(base + s.from, base + s.to, { class: s.cls }))
    return false
  })
  return DecorationSet.create(doc, decos)
}

// ---------------------------------------------------------------------------
// Tastenkürzel: Markdown-Zeichen statt Marks
// ---------------------------------------------------------------------------

function wrapSelection(editor: Editor, marker: string): boolean {
  const { from, to, empty } = editor.state.selection
  if (empty) {
    return editor
      .chain()
      .insertContentAt(from, marker + marker)
      .setTextSelection(from + marker.length)
      .run()
  }
  const text = editor.state.doc.textBetween(from, to, '\n', OBJECT_CHAR)
  return editor
    .chain()
    .insertContentAt({ from, to }, marker + text + marker)
    .setTextSelection({ from: from + marker.length, to: to + marker.length })
    .run()
}

function insertLink(editor: Editor): boolean {
  const { from, to, empty } = editor.state.selection
  const text = empty ? 'Text' : editor.state.doc.textBetween(from, to, '\n', OBJECT_CHAR)
  const snippet = `[${text}](url)`
  const urlFrom = from + text.length + 3
  return editor
    .chain()
    .insertContentAt({ from, to }, snippet)
    .setTextSelection({ from: urlFrom, to: urlFrom + 3 })
    .run()
}


/** Setzt oder entfernt ein Zeilenpräfix (`## `, `- `, `> `) am Absatz. */
function setBlockPrefix(editor: Editor, prefix: string): boolean {
  const { $from } = editor.state.selection
  const block = $from.parent
  if (!block.isTextblock) return false
  const start = $from.start()
  const text = textOf(block)
  const existing = RE_BLOCK_PREFIX.exec(text)?.[0] ?? ''
  const next = existing === prefix ? '' : prefix
  const chain = editor.chain()
  if (existing) chain.deleteRange({ from: start, to: start + existing.length })
  if (next) chain.insertContentAt(start, next)
  return chain.run()
}

/** Enter in einer Liste: Marker fortführen, leeren Punkt beenden. */
function continueList(editor: Editor): boolean {
  const { $from, empty } = editor.state.selection
  if (!empty) return false
  const block = $from.parent
  if (!block.isTextblock) return false
  const text = textOf(block)
  const lineStart = text.lastIndexOf('\n') + 1
  const next = nextListMarker(text.slice(lineStart))
  if (!next) return false
  const start = $from.start()
  if (next.emptyItem) {
    // Leerer Listenpunkt: Marker entfernen, kein neuer Absatz.
    return editor.chain().deleteRange({ from: start + lineStart, to: start + lineStart + next.length }).run()
  }
  if ($from.parentOffset !== block.content.size) return false
  return editor.chain().splitBlock().insertContent(next.prefix).run()
}

export const MarkdownSource = Extension.create<MarkdownSourceOptions>({
  name: 'markdownSource',
  // Vor StarterKit, damit Cmd+B hier landet und keine Mark entsteht.
  priority: 1000,

  addOptions() {
    return { enabled: true }
  },

  addKeyboardShortcuts() {
    const on = () => PLUGIN_KEY.getState(this.editor.state)?.enabled ?? false
    const softBreak = () => {
      const { state, view } = this.editor
      view.dispatch(state.tr.insertText('\n').scrollIntoView())
      return true
    }
    const guard = (fn: () => boolean) => () => (on() ? fn() : false)
    return {
      'Mod-b': guard(() => wrapSelection(this.editor, '**')),
      'Mod-i': guard(() => wrapSelection(this.editor, '*')),
      'Mod-e': guard(() => wrapSelection(this.editor, '`')),
      'Mod-Shift-s': guard(() => wrapSelection(this.editor, '~~')),
      'Mod-k': guard(() => insertLink(this.editor)),
      // Unterstreichen hat kein Markdown: Mark unterdrücken.
      'Mod-u': guard(() => true),
      'Mod-Alt-0': guard(() => setBlockPrefix(this.editor, '')),
      'Mod-Alt-1': guard(() => setBlockPrefix(this.editor, '# ')),
      'Mod-Alt-2': guard(() => setBlockPrefix(this.editor, '## ')),
      'Mod-Alt-3': guard(() => setBlockPrefix(this.editor, '### ')),
      'Mod-Alt-4': guard(() => setBlockPrefix(this.editor, '#### ')),
      'Mod-Alt-5': guard(() => setBlockPrefix(this.editor, '##### ')),
      'Mod-Alt-6': guard(() => setBlockPrefix(this.editor, '###### ')),
      'Mod-Shift-8': guard(() => setBlockPrefix(this.editor, '- ')),
      'Mod-Shift-7': guard(() => setBlockPrefix(this.editor, '1. ')),
      'Mod-Shift-9': guard(() => setBlockPrefix(this.editor, '- [ ] ')),
      'Mod-Shift-b': guard(() => setBlockPrefix(this.editor, '> ')),
      'Mod-Alt-c': guard(() => this.editor.chain().insertContent('```').run()),
      Enter: guard(() => continueList(this.editor)),
      // Zeilenumbruch im Absatz als Zeichen, nicht als hardBreak-Knoten (#2247).
      // Der Server sammelt beim Materialisieren nur Text; ein hardBreak-Knoten
      // trägt keinen und verschwand deshalb aus doc_get_text, Pushes und
      // Export. Tabellen und enge Listen wurden zu einer Zeile verklebt.
      // Bewusst ohne guard: Das gilt auch bei abgeschalteter Formatierung.
      'Shift-Enter': softBreak,
      'Mod-Enter': softBreak,
    }
  },

  addProseMirrorPlugins() {
    const initial = this.options.enabled
    return [
      new Plugin<PluginState>({
        key: PLUGIN_KEY,
        state: {
          init: (_cfg, state: EditorState) => ({
            enabled: initial,
            decorations: initial ? buildDecorations(state.doc) : DecorationSet.empty,
          }),
          apply: (tr: Transaction, prev: PluginState, _old: EditorState, next: EditorState) => {
            const set = tr.getMeta(TOGGLE_META) as boolean | undefined
            const enabled = set ?? prev.enabled
            if (!enabled) return { enabled, decorations: DecorationSet.empty }
            if (set !== undefined || tr.docChanged) {
              return { enabled, decorations: buildDecorations(next.doc) }
            }
            return prev
          },
        },
        props: {
          decorations(state) {
            return PLUGIN_KEY.getState(state)?.decorations ?? DecorationSet.empty
          },
          // Einfügen als Quelltext (#2247). Der Standard macht aus jeder
          // Zeile einen eigenen Absatz, der Server trennt Absätze aber mit
          // einer Leerzeile: Eine eingefügte Tabelle oder enge Liste wäre
          // danach zerrissen. Hier trennt nur eine Leerzeile Absätze,
          // einfache Umbrüche bleiben als Zeichen im Absatz. Dateien
          // (Bilder) behandelt der Editor vorher selbst.
          handlePaste(view, event) {
            const cd = event.clipboardData
            if (!cd || !view.editable) return false
            if (Array.from(cd.items || []).some((it) => it.kind === 'file')) return false
            const text = cd.getData('text/plain').replace(/\r\n?/g, '\n')
            if (!text) return false
            const { schema } = view.state
            const parts = text.split(/\n{2,}/).filter((part) => part.length > 0)
            if (parts.length === 0) return false
            if (parts.length === 1) {
              view.dispatch(view.state.tr.insertText(parts[0]).scrollIntoView())
              return true
            }
            const nodes = parts.map((part) => schema.nodes.paragraph.create(null, schema.text(part)))
            // Offen an beiden Enden: Der erste Teil setzt den aktuellen
            // Absatz fort, der letzte geht in dessen Rest über.
            const slice = new Slice(Fragment.fromArray(nodes), 1, 1)
            view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView())
            return true
          },
        },
      }),
    ]
  },
})

/** Quellmodus an- oder abschalten, ohne das Dokument zu verändern. */
export function setMarkdownSource(editor: Editor, enabled: boolean): void {
  editor.view.dispatch(editor.state.tr.setMeta(TOGGLE_META, enabled))
}

export function isMarkdownSource(editor: Editor): boolean {
  return PLUGIN_KEY.getState(editor.state)?.enabled ?? false
}

/** Werkzeugleisten-Schnittstelle für einen TipTap-Editor mit Quellmodus. */
export function tiptapToolbarApi(editor: Editor): MarkdownToolbarApi {
  return {
    wrap: (marker) => { editor.commands.focus(); wrapSelection(editor, marker) },
    prefix: (prefix) => { editor.commands.focus(); setBlockPrefix(editor, prefix) },
    link: () => { editor.commands.focus(); insertLink(editor) },
    fence: () => {
      editor.chain().focus().insertContent([
        { type: 'paragraph', content: [{ type: 'text', text: '```' }] },
        { type: 'paragraph', content: [{ type: 'text', text: 'code' }] },
        { type: 'paragraph', content: [{ type: 'text', text: '```' }] },
      ]).run()
    },
    table: () => {
      editor.chain().focus().insertContent({
        type: 'paragraph',
        content: [{ type: 'text', text: MD_TABLE_SKELETON }],
      }).run()
    },
  }
}
